import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toActionItemContent } from '../../../../models/utils/actionItemContent';
import { useActionItemMutations } from '../../../../queries/ActionItem/useActionItemMutations';
import { Button } from '../../Button';
import { THEME } from '../../Theme/theme';

interface IProps {
	toolCall: Api.ActionItemsToolCall;
}

function formatSuggestedDate(value: string | undefined): string | null {
	if (!value) {
		return null;
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : format(date, 'EEE, MMM d');
}

/**
 * Renders the action items an assistant proposes so the user can add them to Levitate one at a time
 * or all at once. Nothing is created until a button is pressed.
 */
export const ActionItemsToolCallCard: React.FC<IProps> = ({ toolCall }) => {
	const { create } = useActionItemMutations({});
	const [createdIndexes, setCreatedIndexes] = React.useState<Set<number>>(() => new Set());
	const [pendingIndexes, setPendingIndexes] = React.useState<Set<number>>(() => new Set());
	const proposals = (toolCall.actionItems ?? []).filter(proposal => !!proposal.text?.trim());

	if (proposals.length === 0) {
		return null;
	}

	const createOne = (proposal: Api.IEmailExecProposedActionItem, index: number) => {
		const text = proposal.text?.trim() ?? '';
		setPendingIndexes(previous => new Set(previous).add(index));
		create.mutate(
			{
				plainText: text,
				content: toActionItemContent(text),
				dueDate: proposal.suggestedDueDate ?? null,
				hasDueTime: false,
				priority: null,
				assignee: null,
				contacts: proposal.contactId ? [{ id: proposal.contactId } as Api.IContact] : [],
			},
			{
				onSuccess: () => setCreatedIndexes(previous => new Set(previous).add(index)),
				onSettled: () =>
					setPendingIndexes(previous => {
						const next = new Set(previous);
						next.delete(index);
						return next;
					}),
			}
		);
	};

	const remaining = proposals
		.map((proposal, index) => ({ proposal, index }))
		.filter(({ index }) => !createdIndexes.has(index));

	return (
		<div className={css(styleSheet.card)} data-testid='action-items-tool-call-card'>
			<div className={css(styleSheet.header)}>
				<span className={css(styleSheet.title)}>Suggested action items</span>
				{remaining.length > 1 ? (
					<Button
						size='sm'
						variant='secondary'
						onClick={() => remaining.forEach(({ proposal, index }) => createOne(proposal, index))}
					>
						Add all
					</Button>
				) : null}
			</div>
			<ul className={css(styleSheet.list)}>
				{proposals.map((proposal, index) => {
					const created = createdIndexes.has(index);
					const due = formatSuggestedDate(proposal.suggestedDueDate);
					return (
						<li key={`${proposal.sourceMessageId ?? 'proposal'}-${index}`} className={css(styleSheet.item)}>
							<div className={css(styleSheet.itemText)}>
								<span className={css(styleSheet.text, created && styleSheet.textCreated)}>{proposal.text}</span>
								<span className={css(styleSheet.meta)}>
									{[proposal.contactName, due ? `Due ${due}` : null].filter(Boolean).join(' · ')}
								</span>
							</div>
							{created ? (
								<Link className={css(styleSheet.createdLink)} to='/notes-action-items?activeTab=action-items'>
									Added
								</Link>
							) : (
								<Button size='sm' isLoading={pendingIndexes.has(index)} onClick={() => createOne(proposal, index)}>
									Add
								</Button>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
		marginTop: THEME.spacing.sm,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
	},
	title: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	list: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	item: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
	},
	itemText: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	text: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	textCreated: {
		color: THEME.colors.text.light,
	},
	meta: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	createdLink: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.bold,
		whiteSpace: 'nowrap',
	},
});
